import { ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { apiClient } from '../services/api'
import { NewsBanner } from './NewsBanner'
import { Button } from './ui/button'
import { Avatar, AvatarFallback } from './ui/avatar'
import { Separator } from './ui/separator'
import { cn } from '../lib/utils'
import { FeedbackWidget } from './FeedbackWidget'
import { FoundingAccessNotice } from './FoundingAccessNotice'
import { trackPageView } from '../services/feedbackService'
import {
  Shield,
  BarChart3,
  Building2,
  FileText,
  Settings,
  LogOut,
  CreditCard,
  AlertTriangle,
  ScanLine,
  ClipboardList,
  BookOpen,
  Newspaper,
  Menu,
  X,
  Files,
  Megaphone,
  ScrollText,
  Sparkles,
  Bell,
} from 'lucide-react'

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const canReview = user?.role === 'admin' || user?.role === 'assessor'
  const isDemo = new URLSearchParams(location.search).get('demo') === '1'

  // Track page views for feedback readiness
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  const { data: queueData } = useQuery({
    queryKey: ['reviewQueueCount'],
    queryFn: () => apiClient.listReviewQueue('pending'),
    enabled: canReview,
    refetchInterval: 60_000,
  })
  const pendingCount: number = queueData?.data?.count ?? 0

  const { data: eventsData } = useQuery({
    queryKey: ['abuseEventsCount'],
    queryFn: () => apiClient.getAbuseEvents(1),
    refetchInterval: 30_000,
  })
  const unreadAlerts: number = eventsData?.data?.unreadCount ?? 0

  const primaryNav = [
    { name: 'Portfolio', href: '/dashboard', icon: BarChart3 },
    { name: 'Clients', href: '/clients', icon: Building2 },
    canReview ? { name: 'Review', href: '/review', icon: Files, badge: pendingCount } : null,
    { name: 'Outreach', href: '/outreach', icon: Megaphone },
  ].filter(Boolean) as Array<{ name: string; href: string; icon: React.ElementType; badge?: number }>

  const toolsNav = [
    { name: 'Config scanner', href: '/config-scanner', icon: ScanLine },
    { name: 'Pen test import', href: '/scan-import', icon: ClipboardList },
    { name: 'Policy templates', href: '/policy-templates', icon: BookOpen },
    { name: 'Reports', href: '/reports', icon: FileText },
  ]

  const secondaryNav = [
    { name: 'Subscription', href: '/subscription', icon: CreditCard },
    { name: 'Settings', href: '/settings', icon: Settings },
    { name: 'Resources', href: '/resources', icon: Shield },
    user?.role === 'admin' ? { name: 'Audit log', href: '/audit-logs', icon: ScrollText } : null,
    { name: 'Skill Feedback', href: '/feedback', icon: Sparkles },
    { name: 'Blog', href: '/blog', icon: Newspaper },
  ].filter(Boolean) as Array<{ name: string; href: string; icon: React.ElementType }>

  const navigation = [...primaryNav, ...toolsNav, ...secondaryNav]

  const isActive = (href: string) => {
    if (href === '/dashboard') return location.pathname === '/dashboard'
    return location.pathname === href || location.pathname.startsWith(href + '/')
  }

  const userInitials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U'

  return (
    <div className="min-h-screen bg-gray-50">
      {isDemo && (
        <div className="bg-blue-600 text-white text-center text-sm py-1 font-medium relative">
          Demo Mode — Consultant View
          <span className="absolute right-4 top-1 text-xs opacity-70">
            Level2Logic © {new Date().getFullYear()}
          </span>
        </div>
      )}

      {!isDemo && (
      <div className="bg-amber-100 border-b border-amber-200 px-4 py-2 text-center text-sm font-medium text-amber-800 flex justify-center items-center">
        <AlertTriangle className="h-4 w-4 mr-2" />
        Preview environment — do not upload actual Controlled Unclassified Information (CUI).
      </div>
      )}

      <NewsBanner />

      <header className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="flex items-center space-x-3">
                <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
                  <Shield className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h1 className="text-lg font-semibold text-gray-900 leading-tight">Level2Logic</h1>
                  {!isDemo && <p className="text-xs text-muted-foreground leading-tight">CMMC consultant platform</p>}
                </div>
              </Link>
            </div>

            <button
              className="md:hidden p-2 rounded-md text-muted-foreground hover:bg-muted"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>

            {!isDemo && (
            <div className="hidden md:flex items-center space-x-4">
              {/* Alert Bell */}
              <Link
                to="/dashboard"
                className="relative p-2 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                aria-label="Alerts"
              >
                <Bell className="h-5 w-5" />
                {unreadAlerts > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1">
                    {unreadAlerts > 99 ? '99+' : unreadAlerts}
                  </span>
                )}
              </Link>

              <div className="flex items-center space-x-2">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-semibold">
                    {userInitials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-foreground leading-tight">{user?.name}</span>
                  <span className="text-xs text-muted-foreground capitalize leading-tight">{user?.role}</span>
                </div>
              </div>

              <Separator orientation="vertical" className="h-8" />

              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                className="text-muted-foreground hover:text-foreground"
              >
                <LogOut className="h-4 w-4 mr-1.5" />
                Logout
              </Button>
            </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex">
          {!isDemo && (
          <nav className="hidden md:block w-64 bg-white border-r min-h-[calc(100vh-4rem)]">
            <div className="p-4">
              {/* Primary Navigation */}
              <ul className="space-y-0.5">
                {primaryNav.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.href)
                  return (
                    <li key={item.name}>
                      <Button
                        variant="ghost"
                        asChild
                        className={cn(
                          'w-full justify-start px-3 py-2 h-auto font-medium',
                          active
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Link to={item.href} className="flex items-center space-x-3">
                          <Icon className="h-5 w-5" />
                          <span className="flex-1">{item.name}</span>
                          {'badge' in item && item.badge && item.badge > 0 ? (
                            <span className="ml-auto inline-flex items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold min-w-[20px] h-5 px-1">
                              {item.badge}
                            </span>
                          ) : null}
                        </Link>
                      </Button>
                    </li>
                  )
                })}
              </ul>

              <Separator className="my-3" />

              {/* Tools Section */}
              <p className="px-3 py-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Tools
              </p>
              <ul className="space-y-0.5 mt-1">
                {toolsNav.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.href)
                  return (
                    <li key={item.name}>
                      <Button
                        variant="ghost"
                        asChild
                        className={cn(
                          'w-full justify-start px-3 py-2 h-auto font-medium',
                          active
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Link to={item.href} className="flex items-center space-x-3">
                          <Icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </Link>
                      </Button>
                    </li>
                  )
                })}
              </ul>

              <Separator className="my-3" />

              {/* Secondary Navigation */}
              <ul className="space-y-0.5">
                {secondaryNav.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.href)
                  return (
                    <li key={item.name}>
                      <Button
                        variant="ghost"
                        asChild
                        className={cn(
                          'w-full justify-start px-3 py-2 h-auto font-medium',
                          active
                            ? 'bg-muted text-foreground'
                            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                        )}
                      >
                        <Link to={item.href} className="flex items-center space-x-3">
                          <Icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </Link>
                      </Button>
                    </li>
                  )
                })}
              </ul>
            </div>
          </nav>
          )}

          <main className="flex-1 p-6">
            {/* Demo watermark overlay */}
            {isDemo && (
              <div className="fixed bottom-4 right-4 pointer-events-none z-50 opacity-30 rotate-[-15deg]">
                <p className="text-3xl font-bold text-gray-500">DEMO</p>
              </div>
            )}
            {/* Mobile Menu Panel */}
            {mobileMenuOpen && (
              <div className="md:hidden border-t border-border bg-white" id="mobile-menu">
                <div className="px-4 py-3 space-y-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-3 text-muted-foreground hover:bg-muted rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-ring"
                      aria-label={item.name}
                    >
                      <span className="flex-1">{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {/* Founding-partner end date / read-only upgrade prompt (ADR-0029) */}
            <FoundingAccessNotice />
            {children}
          </main>
        </div>
      </div>
      <FeedbackWidget />
    </div>
  )
}
